import dayjs from './dayjs';
import './reportExport.css';

export type ReportExportValue = string | number | null;

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const EXCEL_EPOCH_OFFSET = 25569;
const XLSX_MAX_ROWS = 1048576;
const XLSX_MAX_COLUMNS = 16384;

const downloadBlob = (blob: Blob, outputFileName: string, extension: 'csv' | 'xlsx') => {
	const link = document.createElement('a');
	const url = URL.createObjectURL(blob);
	link.href = url;
	link.download = `${outputFileName}__${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.${extension}`;
	document.body.appendChild(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(url);
};

export const downloadCsvFile = (csvContent: string, outputFileName: string) => {
	downloadBlob(
		new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }),
		outputFileName,
		'csv',
	);
};

/** Escape a report value so line breaks and delimiters remain in one CSV cell. */
export const escapeReportCsvCell = (value: string | number): string => {
	const text = String(value);
	return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Parse the already selected CSV export without splitting quoted commas or line breaks. */
export const parseReportCsv = (content: string): string[][] => {
	const rows: string[][] = [];
	let row: string[] = [];
	let field = '';
	let quoted = false;
	const csv = content.charCodeAt(0) === 0xfeff ? content.slice(1) : content;

	for (let index = 0; index < csv.length; index += 1) {
		const char = csv[index];
		if (char === '"') {
			if (quoted && csv[index + 1] === '"') {
				field += '"';
				index += 1;
			} else if (quoted || field === '') {
				quoted = !quoted;
			} else {
				field += char;
			}
		} else if (char === ',' && !quoted) {
			row.push(field);
			field = '';
		} else if ((char === '\n' || char === '\r') && !quoted) {
			row.push(field);
			rows.push(row);
			row = [];
			field = '';
			if (char === '\r' && csv[index + 1] === '\n') index += 1;
		} else if (char === '\r' && quoted && csv[index + 1] === '\n') {
			field += '\n';
			index += 1;
		} else {
			field += char;
		}
	}

	if (quoted) throw new Error('The CSV export contains an unclosed quoted field.');
	if (field !== '' || row.length > 0) {
		row.push(field);
		rows.push(row);
	}
	return rows;
};

const isIdentifierColumn = (header: string) =>
	/\b(id|identifier|username|phone|mobile|telephone|zip|postal|code|reference|ref|ticket|iban|swift|ip address)\b/i.test(
		header,
	) ||
	/^(account|user|order|position|login)$/i.test(header.trim()) ||
	/\b(account|user|order|position)\b.*\b(number|no\.?|name)\b/i.test(header);

const isNumericColumn = (header: string) =>
	/^(#|No\.?)$/i.test(header.trim()) ||
	/\b(amount|balance|equity|price|volume|quantity|count|total|commission|profit|loss|swap|leverage|margin|deposit|withdraw|credit|debit|fee|rate|percentage|percent|days|lots|spread|net|tax|value)\b|P\/L/i.test(
		header,
	);

const isDateColumn = (header: string) =>
	/\b(date|time|timestamp|created|updated|opened|closed|settled|expiry|expiration|expires)\b/i.test(
		header,
	);

const parseExcelDate = (header: string, value: string): number | null => {
	if (!isDateColumn(header)) return null;
	const match =
		/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?$/.exec(
			value,
		);
	if (!match) return null;
	const [, year, month, day, hour = '0', minute = '0', second = '0', millisecond = '0'] = match;
	const utc = Date.UTC(
		Number(year),
		Number(month) - 1,
		Number(day),
		Number(hour),
		Number(minute),
		Number(second),
		Number(millisecond.padEnd(3, '0')),
	);
	const date = new Date(utc);
	if (
		date.getUTCFullYear() !== Number(year) ||
		date.getUTCMonth() + 1 !== Number(month) ||
		date.getUTCDate() !== Number(day)
	)
		return null;
	return utc / 86400000 + EXCEL_EPOCH_OFFSET;
};

export const getReportExportValue = (header: string, rawValue: string): ReportExportValue => {
	if (rawValue === '') return null;
	const value = rawValue.trim();
	if (value === '') return rawValue;
	const date = parseExcelDate(header, value);
	if (date !== null) return date;
	if (isIdentifierColumn(header) || /^[-+]?0\d/.test(value)) return rawValue;
	const numeric =
		isNumericColumn(header) && /^[-+]?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?$/.test(value);
	if (numeric) {
		const digits = value.replace(/[^\d]/g, '');
		if (digits.length <= 15) {
			const number = Number(value.replace(/,/g, ''));
			if (Number.isFinite(number)) return number;
		}
	}
	return rawValue;
};

export const buildReportWorkbook = async (csvContent: string) => {
	const { default: ExcelJS } = await import('exceljs');
	const workbook = new ExcelJS.Workbook();
	const sheet = workbook.addWorksheet('Report', {
		views: [{ state: 'frozen', ySplit: 1 }],
	});
	const [headers = [], ...rows] = parseReportCsv(csvContent);
	if (headers.length === 0) throw new Error('The report has no columns to export.');
	if (headers.length > XLSX_MAX_COLUMNS || rows.length + 1 > XLSX_MAX_ROWS) {
		throw new Error('The report exceeds the XLSX worksheet size limit.');
	}
	rows.forEach((row, index) => {
		if (row.length !== headers.length) {
			throw new Error(`Report row ${index + 2} does not match the selected columns.`);
		}
	});
	sheet.addRow(headers);
	sheet.getRow(1).font = { bold: true };
	sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: headers.length } };

	rows.forEach((row) => {
		const cells = headers.map((header, index) =>
			getReportExportValue(header, row[index] ?? ''),
		);
		const xlsxRow = sheet.addRow(cells);
		xlsxRow.eachCell((cell, columnNumber) => {
			if (
				typeof cell.value === 'number' &&
				parseExcelDate(headers[columnNumber - 1], row[columnNumber - 1] ?? '') !== null
			) {
				cell.numFmt = Number.isInteger(cell.value) ? 'yyyy-mm-dd' : 'yyyy-mm-dd hh:mm:ss';
			}
		});
	});

	sheet.columns.forEach((column, index) => {
		column.width = Math.min(48, Math.max(12, headers[index].length + 2));
	});
	return workbook;
};

export const downloadXlsxFile = async (csvContent: string, outputFileName: string) => {
	const workbook = await buildReportWorkbook(csvContent);
	const buffer = await workbook.xlsx.writeBuffer();
	downloadBlob(new Blob([buffer], { type: XLSX_MIME }), outputFileName, 'xlsx');
};

/** Give every report using DownloadFile the same CSV and XLSX choices. */
export const showReportExportDialog = (csvContent: string, outputFileName: string) => {
	const dialog = document.createElement('dialog');
	dialog.className = 'report-export-dialog';
	dialog.setAttribute('aria-labelledby', 'report-export-title');
	dialog.setAttribute('aria-describedby', 'report-export-description');
	const title = document.createElement('h2');
	title.id = 'report-export-title';
	title.textContent = 'Download report';
	const description = document.createElement('p');
	description.id = 'report-export-description';
	description.textContent = 'Choose a file format for the current report selection.';
	const actions = document.createElement('div');
	actions.className = 'report-export-actions';
	const csvButton = document.createElement('button');
	csvButton.type = 'button';
	csvButton.className = 'btn btn-outline-primary';
	csvButton.textContent = 'CSV';
	const xlsxButton = document.createElement('button');
	xlsxButton.type = 'button';
	xlsxButton.className = 'btn btn-success';
	xlsxButton.textContent = 'Excel (.xlsx)';
	const cancelButton = document.createElement('button');
	cancelButton.type = 'button';
	cancelButton.className = 'btn btn-link';
	cancelButton.textContent = 'Cancel';
	const status = document.createElement('p');
	status.className = 'report-export-status';
	status.setAttribute('role', 'status');
	actions.append(csvButton, xlsxButton, cancelButton);
	dialog.append(title, description, actions, status);
	document.body.appendChild(dialog);
	dialog.addEventListener('close', () => dialog.remove(), { once: true });
	csvButton.addEventListener('click', () => {
		dialog.close();
		downloadCsvFile(csvContent, outputFileName);
	});
	xlsxButton.addEventListener('click', async () => {
		csvButton.disabled = true;
		xlsxButton.disabled = true;
		cancelButton.disabled = true;
		status.textContent = 'Preparing Excel file...';
		try {
			await downloadXlsxFile(csvContent, outputFileName);
			dialog.close();
		} catch (error) {
			console.error('Unable to export XLSX report:', error);
			status.textContent = 'Unable to create the Excel file. Please try again.';
			csvButton.disabled = false;
			xlsxButton.disabled = false;
			cancelButton.disabled = false;
		}
	});
	cancelButton.addEventListener('click', () => dialog.close());
	if (typeof dialog.showModal === 'function') {
		dialog.showModal();
	} else {
		dialog.remove();
		downloadCsvFile(csvContent, outputFileName);
	}
};
